import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ChevronLeftIcon } from "@App/Components/common/Icons";
import { type FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

const IMAGES = [
  {
    src: "/images/partner/mobilize/002_Vertragsnummer_Angebot.png",
    captionKey: "contractNumberHelp.captions.offer",
  },
  {
    src: "/images/partner/mobilize/001_Vertragsnummer_Verbindliche_Bestellung.png",
    captionKey: "contractNumberHelp.captions.confirmation",
  },
  {
    src: "/images/partner/mobilize/003_Vertragsnummer_KUPO_Bestellungen.png",
    captionKey: "contractNumberHelp.captions.overview",
  },
  {
    src: "/images/partner/mobilize/004_Vertragsnummer_KUPO_DETAILANSICHT.png",
    captionKey: "contractNumberHelp.captions.detail",
  },
];

export const ContractNumberHelpIcon: FunctionComponent = () => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const step = (direction: 1 | -1) => {
    setIndex((current) => (current + direction + IMAGES.length) % IMAGES.length);
  };

  const current = IMAGES[index];

  return (
    <Popover className="relative inline-flex items-center">
      <PopoverButton
        aria-label={t("contractNumberHelp.ariaLabel")}
        className="inline-flex h-5 w-5 items-center justify-center text-primary"
      >
        <InformationCircleIcon className="h-5 w-5" aria-hidden="true" />
      </PopoverButton>
      <PopoverPanel
        anchor={{ to: "bottom start", gap: 8, padding: 16 }}
        className="z-20 w-[34rem] max-w-[92vw] max-h-[85vh] overflow-y-auto rounded-lg border border-neutral-300 bg-white p-4 shadow-lg"
      >
        <p className="mb-3 text-center font-bold text-neutral-800">
          {t("contractNumberHelp.heading")}
        </p>
        <button
          type="button"
          data-testid="contract-number-help-thumbnail"
          aria-label={t(current.captionKey)}
          onClick={() => setIsFullscreen(true)}
          className="mb-2 block w-full cursor-zoom-in"
        >
          <img
            src={current.src}
            alt={t(current.captionKey)}
            className="w-full rounded border border-neutral-200"
          />
        </button>
        <p className="mb-3 text-center text-sm font-semibold text-neutral-700">
          {t(current.captionKey)}
        </p>
        <div className="flex items-center justify-between">
          <button
            type="button"
            aria-label={t("contractNumberHelp.previous")}
            onClick={() => step(-1)}
            className="flex h-7 w-7 items-center justify-center rounded bg-neutral-100"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <div className="flex gap-1">
            {IMAGES.map((image, i) => (
              <span
                key={image.src}
                data-testid="contract-number-help-dot"
                className={`h-1.5 w-1.5 rounded-full ${i === index ? "bg-primary" : "bg-neutral-300"}`}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label={t("contractNumberHelp.next")}
            onClick={() => step(1)}
            className="flex h-7 w-7 items-center justify-center rounded bg-neutral-100"
          >
            <ChevronLeftIcon className="h-4 w-4 rotate-180" />
          </button>
        </div>
      </PopoverPanel>
      {isFullscreen && (
        <div
          role="button"
          tabIndex={0}
          aria-label={t("contractNumberHelp.closeFullscreen")}
          onClick={() => setIsFullscreen(false)}
          onKeyDown={(e) => e.key === "Escape" && setIsFullscreen(false)}
          className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/80 p-4"
        >
          <img
            data-testid="contract-number-help-fullscreen-image"
            src={current.src}
            alt={t(current.captionKey)}
            className="max-h-[90vh] max-w-[90vw] rounded shadow-2xl"
          />
        </div>
      )}
    </Popover>
  );
};
